import { useQuery } from "@apollo/client";
import { useEffect, useMemo } from "react";
import { GET_FILTERS_DATA } from "../Findings/FindingsFilters/FindingsFilterApi";
import { filterFieldsEnum } from "../Findings/FindingsFilters/FilterRow/FilterRowUtils";
import { getAdditionalDataTitle } from "../Findings/FindingDetailsPane/FindingsDetailsPaneComponents/Details/DetailsUtils";
import useSeverityAsPriority from "../General/useSeverityAsPriority";
import useRemediationItemsSplit from "../General/useRemediationItemsSplit";
import { resolveCatalogContext } from "./FilterManagement/filterEntity";
import {
  isActualStatusKey,
  splitFilterKey,
} from "./FilterManagement/filterKey";
import { findingSeverities, findingStatuses } from "../Findings/FindingsUtils";
import useLogger, {
  FILTERS_DATA_QUERY_ERROR,
  LOG_SEVERITY,
  scrubbedApolloErrorExtra,
} from "../Logger/useLogger";
import type {
  FilterData,
  ProcessedFieldData,
  UseFiltersDataParams,
  UseFiltersDataReturn,
} from "../types/useFiltersData";

interface GetFiltersDataResponse {
  finding_filters_data?: (FilterData | null)[] | null;
}

export const FILTER_OPTIONS_ERROR_MESSAGE =
  "Couldn't load filter options. Try again later";

// Fields whose display label follows the tenant's Severity/Priority terminology. Resolved here once,
// so every consumer (filter menu, chips, filter search, finding details) just reads `field.label`.
const dynamicFieldLabels: Record<string, (severityTerm: string) => string> = {
  [filterFieldsEnum.SEVERITY]: (term) => term,
  [filterFieldsEnum.AI_ANALYST_PRIORITY]: (term) =>
    `AI analyst ${term.toLocaleLowerCase()}`,
  [filterFieldsEnum.AI_ANALYST_PRIORITY_DIRECTION]: (term) =>
    `AI analyst ${term.toLocaleLowerCase()} direction`,
};

const useFiltersData = ({
  filterType,
  filterContext,
}: UseFiltersDataParams): UseFiltersDataReturn => {
  const { logError } = useLogger();
  const { severityDisplay } = useSeverityAsPriority();
  const isRemediationItemsSplit = useRemediationItemsSplit();
  const catalogContext = resolveCatalogContext(
    filterContext,
    isRemediationItemsSplit
  );

  const {
    data: filtersData,
    loading: fieldsLoading,
    error: fieldsError,
  } = useQuery<GetFiltersDataResponse>(GET_FILTERS_DATA, {
    variables: { filterType: filterType, context: catalogContext },
    errorPolicy: "all",
  });

  const fieldsData = useMemo<ProcessedFieldData[]>(() => {
    const filters = filtersData?.finding_filters_data;

    if (!filters?.length) {
      return [];
    }

    return filters
      .filter((filter): filter is FilterData => !!filter)
      .map((filter) => {
        const keyParts = splitFilterKey(filter.field);
        const label =
          dynamicFieldLabels[keyParts.property]?.(severityDisplay) ??
          filter.display_name ??
          getAdditionalDataTitle(keyParts.property);
        // Mirrors the BE default of true.
        const field = {
          ...filter,
          ...keyParts,
          searchable: filter.is_searchable ?? true,
          label,
        };

        if (
          keyParts.property === filterFieldsEnum.SEVERITY ||
          keyParts.property === filterFieldsEnum.AI_ANALYST_PRIORITY
        ) {
          return { ...field, values: Object.keys(findingSeverities) };
        }

        if (isActualStatusKey(field.field)) {
          return { ...field, values: Object.keys(findingStatuses) };
        }

        return field;
      }) as ProcessedFieldData[];
  }, [filtersData, severityDisplay]);

  const hasFieldsError = !fieldsLoading && !!fieldsError && !fieldsData.length;

  // Error identity alone, so one failure logs once.
  useEffect(() => {
    if (!fieldsError) {
      return;
    }

    logError(new Error(FILTERS_DATA_QUERY_ERROR), FILTERS_DATA_QUERY_ERROR, {
      severity: LOG_SEVERITY.CRITICAL,
      tags: {
        feature: "filter_metadata",
        filterType,
        filterContext: catalogContext,
      },
      extra: {
        operation: "get_filters_data",
        returnedFieldCount: fieldsData.length,
        ...scrubbedApolloErrorExtra(fieldsError),
      },
    });
  }, [fieldsError]);

  return { fieldsData, fieldsLoading, fieldsError, hasFieldsError };
};

export default useFiltersData;
